'use client'

import Button from '@/components/ui/CustomButton'
import { Button as CustomButton } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import { PatientSalutations, PatientSalutationsValues } from '@/lib/generated-enums'
import { Doctor } from '@/lib/types/doctor'
import { MyPrescriberListingMode } from '@/lib/types/prescriber'
import { uploadFile } from '@/lib/uploadFIle'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { X } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

type GetInTouchData = {
  prescriber?: number
  salutation: number
  gender: number
  description: string
  email: string
  name: string
  phone_number: string
}

const formSchema = z.object({
  salutation: z.enum(['0', '1', '2'], {
    errorMap: () => ({ message: 'Please select a valid salutation' }),
  }),
  name: z.string().min(2, 'Please enter atleast 3 characters'),
  description: z.string().min(2, 'Please enter atleast 3 characters'),
  gender: z.enum(['0', '1', '2'], {
    errorMap: () => ({ message: 'Please select a gender' }),
  }),
  phone_number: z.string().refine(isValidPhoneNumber, {
    message: 'Please enter a valid phone number',
  }),
  email: z.string().email('Please enter a valid email'),
  medical_document: z.any().optional(),
})

export type GetInTouchFormType = z.infer<typeof formSchema>

export default function GetInTouch({
  prescriber,
  className,
}: {
  prescriber?: MyPrescriberListingMode | Doctor
  className?: string
}) {
  const { data: session } = useSession()
  const [open, setOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const form = useForm<GetInTouchFormType>({
    defaultValues: {
      salutation: '0',
      name: '',
      description: '',
      gender: '0',
      email: '',
      phone_number: '',
      medical_document: null,
    },
    reValidateMode: 'onChange',
    mode: 'onChange',

    resolver: zodResolver(formSchema),
  })

  async function addGetInTouchRequest(sessionToken: string, data: GetInTouchData) {
    const url = new URL(`${process.env.NEXT_PUBLIC_API_URL}/patients/patient-care-inquiries/`)

    try {
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        },
        body: JSON.stringify(data),
      })

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Your request has been submitted!',
          variant: 'success',
        })
        return true
      } else {
        const errorData = await response.json().catch(() => ({}))
        toast({
          title: 'Error',
          description: errorData?.message || 'Failed to submit your request.',
          variant: 'error',
        })
        return false
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Network error. Please try again.',
        variant: 'error',
      })
      return false
    }
  }

  const onSubmit = async (data: GetInTouchFormType) => {
    let medicalDocumentId = null
    if (data.medical_document) {
      const uploadResult = await uploadFile({
        file: data.medical_document,
        token: session?.access_token,
      })
      medicalDocumentId = uploadResult?.id
    }
    const newData = {
      prescriber: prescriber?.id,
      salutation: Number(data.salutation),
      gender: Number(data.gender),
      description: data.description,
      email: data.email,
      name: data.name,
      phone_number: data.phone_number,
      medical_document: medicalDocumentId,
    }
    const success = await addGetInTouchRequest(session?.access_token || '', newData)
    if (success) setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          label='Get In Touch'
          className={cn(
            `
              mt-2 rounded-lg border-2 border-[#F59405] bg-white px-10 py-2
              text-center text-base font-bold text-[#F59405]

              hover:text-white
            `,
            className
          )}
          onClick={() => setOpen(true)}
        />
      </DialogTrigger>
      <DialogContent
        className={`
          h-[80vh] overflow-y-auto rounded-xl bg-white p-4 scrollbar-thin
          scrollbar-thumb-gray-300 scrollbar-track-gray-100
          scrollbar-thumb-rounded-full scrollbar-track-rounded-full

          md:mx-4 md:w-1/2

          scrollbar-thumb-hover:bg-gray-400

          scrollbar-track-hover:bg-gray-200

          sm:mx-2 sm:w-full

          xs:mx-1 xs:w-full
        `}
      >
        <DialogHeader>
          <DialogTitle>Get In Touch</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className='mt-2 flex flex-col gap-2' onSubmit={form.handleSubmit(onSubmit)}>
            <div className='flex w-full flex-row items-center gap-2'>
              <FormField
                control={form.control}
                name='salutation'
                render={({ field }) => (
                  <FormItem className='flex flex-col'>
                    <FormLabel className='text-sm'>Salutation</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className={`
                            h-14 text-base font-medium

                            md:w-full

                            sm:w-[80%]

                            xs:w-[90%]
                          `}
                        >
                          <SelectValue placeholder='' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className='bg-white'>
                        {Object.keys(PatientSalutations)
                          .filter((key) => isNaN(Number(key)))
                          .map((key) => (
                            <SelectItem
                              key={PatientSalutations[key as keyof typeof PatientSalutations]}
                              value={PatientSalutations[key as keyof typeof PatientSalutations].toString()}
                            >
                              {PatientSalutationsValues[PatientSalutations[key as keyof typeof PatientSalutations]]}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='flex w-full flex-col'>
                    <FormLabel className='text-sm'>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='First Name'
                        className={`
                          text-base font-medium

                          md:w-full

                          sm:w-[80%]

                          xs:w-[90%]
                        `}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name='phone_number'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      defaultCountry={'GB'}
                      placeholder='Enter Phone Number'
                      {...field}
                      className='z-40'
                      countrySelectProps={{ modal: true }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='flex flex-col'>
                  <FormLabel className='text-sm'>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type='email'
                      placeholder='Email'
                      className={`
                        text-base font-medium

                        md:w-full

                        sm:w-[80%]

                        xs:w-[90%]
                      `}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='gender'
              render={({ field }) => (
                <FormItem className='flex flex-col'>
                  <FormLabel className='text-sm'>Gender</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className='h-14 text-base font-medium'>
                        <SelectValue placeholder='Select gender' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className='bg-white'>
                      <SelectItem value='0'>Female</SelectItem>
                      <SelectItem value='1'>Male</SelectItem>
                      <SelectItem value='2'>Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Health Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder='Type Something' className={`
                      rounded-xl text-base font-medium
                    `} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='medical_document'
              render={({ field }) => (
                <FormItem className='flex flex-col'>
                  <FormLabel className='text-sm'>Medical Document</FormLabel>
                  <FormControl>
                    <div className='flex items-center gap-2'>
                      <input
                        ref={fileInputRef}
                        type='file'
                        accept='.pdf,.jpg,.jpeg,.png'
                        className='hidden'
                        onChange={(e) => field.onChange(e.target.files?.[0] || null)}
                      />
                      <CustomButton type='button' variant='outline' onClick={() => fileInputRef.current?.click()}>
                        {field.value ? 'Change File' : 'Upload File'}
                      </CustomButton>
                      {field.value && (
                        <span
                          className={`
                            flex items-center gap-1 text-sm text-gray-700
                          `}
                        >
                          {field.value.name}
                          <button
                            type='button'
                            onClick={() => field.onChange(null)}
                            className={`
                              ml-1 text-red-500

                              hover:text-red-700
                            `}
                            title='Remove file'
                          >
                            <X size={16} />
                          </button>
                        </span>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              label='Continue'
              className='mt-2'
              disabled={form.formState.isSubmitting}
              loading={form.formState.isSubmitting}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
